import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import './CartPopup.css';

const CartPopup = () => {
  const navigate = useNavigate();
  const { 
    items, 
    getCartTotal, 
    getCartCount, 
    updateQuantity, 
    removeFromCart, 
    applyCoupon, 
    removeCoupon,
    appliedCoupon,
    getDiscountAmount,
    getItemTotalPrice,
    getFinalTotal,
    isCartPopupVisible,
    setCartPopupVisible
  } = useCart();
  const [isOpening, setIsOpening] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [showOfferCode, setShowOfferCode] = useState(false);
  const [couponStatus, setCouponStatus] = useState(null); // 'success' or 'error'

  useEffect(() => {
    if (isCartPopupVisible) {
      setTimeout(() => setIsOpening(true), 10);
      document.body.style.overflow = 'hidden';
    } else {
      setIsOpening(false);
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isCartPopupVisible]);

  const handleClose = () => {
    setIsOpening(false);
    setTimeout(() => {
      setCartPopupVisible(false);
    }, 400); // Wait for transition
  };

  const handlePlaceOrder = () => {
    handleClose();
    navigate('/checkout');
  };

  const handleApplyCoupon = () => {
    if (couponInput.toUpperCase() === 'NEXA5') {
      applyCoupon({
        code: 'NEXA5',
        type: 'percentage',
        value: 5,
        description: '5% NEXAWAVE Discount'
      });
      setCouponInput('');
      setCouponStatus('success');
      setTimeout(() => setCouponStatus(null), 3000);
    } else {
      setCouponStatus('error');
      setTimeout(() => setCouponStatus(null), 2000);
    }
  };

  const freeItems = [
    {
      cartId: 'free-bag-bonus',
      id: 'free-bag',
      name: 'Premium Laptop Bag (Bonus)',
      imageUrls: ['/images/bonus/bag.jpg'],
      price: 0,
      quantity: 1,
      isFreeBonus: true
    },
    {
      cartId: 'free-antivirus-bonus',
      id: 'free-antivirus',
      name: 'Quick Heal Antivirus (Bonus)',
      imageUrls: ['/images/bonus/quickheal.webp'],
      price: 0,
      quantity: 1,
      isFreeBonus: true
    }
  ];

  const allVisibleItems = items.length > 0 ? [...items, ...freeItems] : [];

  if (!isCartPopupVisible && !isOpening) return null;

  const formatPrice = (price) => `₹${price?.toLocaleString() || '0'}`;

  const calculateTotalSavings = () => {
    const itemSavings = items.reduce((total, item) => {
      if (item.originalPrice && item.originalPrice > item.price) {
        return total + ((item.originalPrice - item.price) * item.quantity);
      }
      return total;
    }, 0);

    return itemSavings + (getDiscountAmount ? getDiscountAmount() : 0);
  };

  const totalSavings = calculateTotalSavings();

  return (
    <>
      {/* Semi-transparent Backdrop */}
      <div 
        className={`cart-popup-backdrop ${isOpening ? 'visible' : ''}`}
        onClick={handleClose}
      />
      
      {/* Slide-out Sidebar Cart */}
      <div className={`side-cart ${isOpening ? 'visible' : ''}`}>
        {/* Header */}
        <div className="side-cart-header">
          <h2>Your Cart ({getCartCount()} {getCartCount() === 1 ? 'Item' : 'Items'})</h2>
          <button className="close-cart-btn" onClick={handleClose} aria-label="Close cart">
            <X size={22} />
          </button>
        </div>

        {/* Cart Content */}
        <div className="side-cart-items">
          {items.length === 0 ? (
            <div className="empty-cart-state">
              <ShoppingBag size={48} strokeWidth={1.5} className="empty-cart-icon" />
              <p>Your shopping bag is currently empty.</p>
              <button 
                className="explore-btn" 
                onClick={() => { handleClose(); navigate('/products'); }}
              >
                Explore Products
                <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            allVisibleItems.map((item) => (
              <div key={item.cartId} className="side-cart-item">
                <div className="item-image">
                  <img 
                    src={item.imageUrls?.[0] || item.image || '/api/placeholder/100/100'} 
                    alt={item.name} 
                    loading="lazy"
                  />
                </div>
                <div className="item-details">
                  <h3 className="item-name">{item.name}</h3>
                  
                  {!item.isFreeBonus && (
                    <div className="item-variant-breakdown">
                      <div className="variant-line">
                        <span className="variant-label">RAM:</span>
                        <span className="variant-value">
                          {item.selectedRAM?.name || (typeof item.baseSpecs?.ramSize === 'object' ? item.baseSpecs.ramSize.name : item.baseSpecs?.ramSize) || 'Standard'}
                          {item.selectedRAM?.priceIncrease > 0 && ` (+${formatPrice(item.selectedRAM.priceIncrease)})`}
                        </span>
                      </div>
                      <div className="variant-line">
                        <span className="variant-label">STORAGE:</span>
                        <span className="variant-value">
                          {item.selectedStorage?.name || (typeof item.baseSpecs?.storageSize === 'object' ? item.baseSpecs.storageSize.name : item.baseSpecs?.storageSize) || 'Standard'}
                          {item.selectedStorage?.priceIncrease > 0 && ` (+${formatPrice(item.selectedStorage.priceIncrease)})`}
                        </span>
                      </div>
                    </div>
                  )}

                  {item.isFreeBonus && (
                    <div className="bonus-badge">Included Free Bonus</div>
                  )}
                  
                  <div className="item-price">{item.isFreeBonus ? 'FREE' : formatPrice(getItemTotalPrice(item) * item.quantity)}</div>
                  
                  {!item.isFreeBonus && (
                    <div className="item-controls">
                      <div className="quantity-picker">
                        <button 
                          className="qty-btn"
                          onClick={() => updateQuantity(item.cartId, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="qty-value">{item.quantity}</span>
                        <button 
                          className="qty-btn"
                          onClick={() => updateQuantity(item.cartId, item.quantity + 1)}
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      
                      <button 
                        className="remove-item-btn"
                        onClick={() => removeFromCart(item.cartId)}
                        aria-label="Remove item"
                      >
                        <Trash2 size={14} strokeWidth={2} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Bottom Section (Summary + Call to Action) */}
        {items.length > 0 && (
          <>
            <div className="coupon-section">
              {appliedCoupon && (
                <div className="applied-coupon-pill">
                  <span>{appliedCoupon.code} applied</span>
                  <button onClick={removeCoupon} className="remove-coupon-btn">Remove</button>
                </div>
              )}
              <div className="coupon-input-group">
                <input 
                  type="text" 
                  placeholder="Enter Coupon Code" 
                  value={couponInput}
                  onChange={(e) => setCouponInput(e.target.value)}
                />
                <button className="apply-btn" onClick={handleApplyCoupon}>Apply</button>
              </div>
              {couponStatus === 'success' && <div className="coupon-feedback success">Coupon Applied Successfully!</div>}
              {couponStatus === 'error' && <div className="coupon-feedback error">Invalid Coupon Code</div>}
              <span className="view-offers" onClick={() => setShowOfferCode(true)}>
                {showOfferCode ? 'Use code: NEXA5' : 'View all offers ›'}
              </span>
            </div>

            <div className="side-cart-footer">
              <div className="summary-row">
                <span className="summary-label">
                  {appliedCoupon ? `Total (after ${appliedCoupon.code})` : 'Sub Total'}
                </span>
                <span className="summary-value" style={{ color: appliedCoupon ? 'var(--nexawave-blue)' : 'inherit' }}>
                  {formatPrice(getFinalTotal())}
                </span>
              </div>
              {appliedCoupon && (
                <div className="summary-row discount-row">
                  <span className="summary-label">
                    Discount ({appliedCoupon.code})
                    <button onClick={removeCoupon} className="inline-remove-btn" title="Remove coupon">×</button>
                  </span>
                  <span className="summary-value">-{formatPrice(getDiscountAmount())}</span>
                </div>
              )}
              <div className="savings-badge">
                You save {formatPrice(totalSavings)} on this order.
              </div>
              <button className="place-order-btn" onClick={handlePlaceOrder}>
                Place Order
                <ArrowRight size={18} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default CartPopup;
